import {Show} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import type {ReactNode} from 'react';
import {useSelector} from 'react-redux';

import {isNotNil} from 'ramda';

import type {
  AdvertisementPreviewPlatform,
  GetAdvertisementPreviewApiResponse,
} from '@dms/api/sales';
import type {GetVehicleApiResponse} from '@dms/api/shared';
import type {GetVehicleApiResponse as GetVehicleV2ApiResponse} from '@dms/api/vehicle';
import {useCountry, useVehicleCatalogue} from '@dms/shared';
import {selectFeatureCategories, selectVehicleFeatures} from '@dms/teas';

import type {Nullish, RequiredTestIdProps} from 'shared/web';

import {getAdvertisementPreviewFeatureGroups} from '../../utils/getAdvertisementPreviewFeatureGroups';
import {getVehicleSpecifications} from '../../utils/getVehicleSpecifications';
import {AdvertisementPreviewDescriptionCard} from './AdvertisementPreviewDescriptionCard';
import {AdvertisementPreviewFeatures} from './AdvertisementPreviewFeatures';
import {
  AdvertisementPreviewGallery,
  type AdvertisementPreviewPhotosQuery,
} from './AdvertisementPreviewGallery';
import {AdvertisementPreviewKeyParameters} from './AdvertisementPreviewKeyParameters';
import {AdvertisementPreviewSectionNav} from './AdvertisementPreviewSectionNav';
import {AdvertisementPreviewSpecifications} from './AdvertisementPreviewSpecifications';
import {AdvertisementPreviewSummary} from './AdvertisementPreviewSummary';
import {getPreviewContainerBreakpoint} from './getPreviewContainerBreakpoint';
import {PREVIEW_CONTAINER_BREAKPOINTS} from './PREVIEW_CONTAINER_BREAKPOINTS';
import {PREVIEW_SECTION_IDS} from './PREVIEW_SECTION_IDS';

export type PreviewContentProps = RequiredTestIdProps & {
  preview: {
    data: GetAdvertisementPreviewApiResponse;
    photosQuery: AdvertisementPreviewPhotosQuery;
    fuelLabel: string | Nullish;
    transmissionLabel: string | Nullish;
    pricingVehicle?: GetVehicleApiResponse;
    specificationsVehicle?: GetVehicleV2ApiResponse;
    isVehicleCatalogueLoading: boolean;
  };
  selection: {
    selectedPlatform?: AdvertisementPreviewPlatform;
    isDefaultLanguage: boolean;
    isLanguagePublished: boolean;
    isTextFetching: boolean;
    languageOptions: Array<{value: string; label: ReactNode}>;
    activeLanguage: string;
    showLanguageTabs: boolean;
  };
  onLanguageChange: (language: string) => void;
  onAddPhotos: () => void;
  vehicleId: string;
};

export function AdvertisementPreviewContent(props: PreviewContentProps) {
  const [catalogue, {isLoading: isCatalogueLoading}] = useVehicleCatalogue(
    props.preview.specificationsVehicle?.type
  );
  const vehicleType = match(props.preview.specificationsVehicle?.type)
    .with(
      Pattern.union(
        'VEHICLETYPE_CARAVAN',
        'VEHICLETYPE_PASSENGER_CAR',
        'VEHICLETYPE_VAN',
        'VEHICLETYPE_MOTORCYCLE',
        'VEHICLETYPE_TRUCK',
        'VEHICLETYPE_SEMI_TRUCK',
        'VEHICLETYPE_SEMI_TRAILER',
        'VEHICLETYPE_TRAILER',
        'VEHICLETYPE_BUS',
        'VEHICLETYPE_OTHER',
        'VEHICLETYPE_AGRICULTURAL_MACHINE',
        'VEHICLETYPE_CONSTRUCTION_MACHINE'
      ),
      (type) => type
    )
    .otherwise(() => undefined);
  const categories = useSelector(selectFeatureCategories(vehicleType));
  const featureOptions = useSelector(selectVehicleFeatures(vehicleType));
  const {getCountryName} = useCountry();
  const formatDate = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const vehicle = props.preview.specificationsVehicle;
  const specifications = isNotNil(vehicle)
    ? getVehicleSpecifications({
        specificationsVehicle: vehicle,
        selectedPlatform: props.selection.selectedPlatform,
        catalogue,
        isCatalogueLoading,
        formatDate,
        formatNumber,
        countryName: getCountryName,
      })
    : null;
  const featureGroups = isNotNil(vehicle)
    ? getAdvertisementPreviewFeatureGroups(vehicle, categories, featureOptions)
    : [];

  return (
    <Columns>
      <AdvertisementPreviewSectionNav
        hasSpecifications={isNotNil(specifications) && specifications.groups.length > 0}
        hasFeatures={featureGroups.length > 0}
      />
      <Hero id={PREVIEW_SECTION_IDS.overview} data-testid={`${props['data-testid']}-overview`}>
        <GalleryColumn>
          <AdvertisementPreviewGallery
            photosQuery={props.preview.photosQuery}
            onAddPhotos={props.onAddPhotos}
            vehicleId={props.vehicleId}
            data-testid={props['data-testid']}
          />
        </GalleryColumn>
        <SummaryColumn>
          <AdvertisementPreviewSummary
            data={props.preview.data}
            selectedPlatform={props.selection.selectedPlatform}
            isDefaultLanguage={props.selection.isDefaultLanguage}
            isLanguagePublished={props.selection.isLanguagePublished}
            isTextFetching={props.selection.isTextFetching}
            pricingVehicle={props.preview.pricingVehicle}
            data-testid={props['data-testid']}
          />
        </SummaryColumn>
        <ParametersRow>
          <AdvertisementPreviewKeyParameters
            data={props.preview.data}
            selectedPlatform={props.selection.selectedPlatform}
            fuelLabel={props.preview.fuelLabel}
            transmissionLabel={props.preview.transmissionLabel}
            specificationsVehicle={vehicle}
            isVehicleCatalogueLoading={props.preview.isVehicleCatalogueLoading}
            data-testid={props['data-testid']}
          />
        </ParametersRow>
      </Hero>
      <Show when={specifications && specifications.groups.length > 0 ? specifications : null}>
        {(groups) => (
          <SpecificationsRow id={PREVIEW_SECTION_IDS.details}>
            <AdvertisementPreviewSpecifications
              specifications={groups}
              data-testid={`${props['data-testid']}-specifications`}
            />
          </SpecificationsRow>
        )}
      </Show>
      <Show when={featureGroups.length > 0}>
        <AdvertisementPreviewFeatures groups={featureGroups} data-testid={props['data-testid']} />
      </Show>
      <DescriptionRow id={PREVIEW_SECTION_IDS.description}>
        <AdvertisementPreviewDescriptionCard
          data={props.preview.data}
          selectedPlatform={props.selection.selectedPlatform}
          isDefaultLanguage={props.selection.isDefaultLanguage}
          isTextFetching={props.selection.isTextFetching}
          languageOptions={props.selection.languageOptions}
          activeLanguage={props.selection.activeLanguage}
          showLanguageTabs={props.selection.showLanguageTabs}
          onLanguageChange={props.onLanguageChange}
          data-testid={props['data-testid']}
        />
      </DescriptionRow>
    </Columns>
  );
}

export const Columns = styled.div`
  container: preview / inline-size;
  display: grid;
  gap: ${({theme}) => theme.getSize(4)};
  align-content: start;
  min-width: 0;
`;
export const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  grid-template-areas: 'gallery summary' 'parameters summary';
  align-items: start;
  column-gap: ${({theme}) => theme.getSize(7)};
  row-gap: ${({theme}) => theme.getSize(4)};
  padding: ${({theme}) => theme.getSize(5)};
  border: 1px solid ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.palettes.white[10][100]};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.wide
  )}) {
    column-gap: ${({theme}) => theme.getSize(5)};
    padding: ${({theme}) => theme.getSize(4)};
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.tablet
  )}) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'gallery' 'summary' 'parameters';
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.compact
  )}) {
    padding: ${({theme}) => theme.getSize(2)};
  }
`;
export const GalleryColumn = styled.div`
  grid-area: gallery;
  min-width: 0;
`;
export const SummaryColumn = styled.div`
  grid-area: summary;
  overflow-wrap: anywhere;
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.getSize(4)};
  min-width: 0;
`;
export const ParametersRow = styled.div`
  grid-area: parameters;
  min-width: 0;
`;
export const SpecificationsRow = styled.section`
  min-width: 0;
`;
export const DescriptionRow = styled.section`
  min-width: 0;
`;
